import { useEffect, useState } from "react";

import { useHomepageSsr } from "@/providers/HomepageSsrProvider";
import { useUser } from "@/providers/useUser";
import type { GeoLocation } from "@/utils/dateTimeFormat/dateTimeInteface";
import DateTimeMoment from "@/utils/dateTimeFormat/dateTimeMoment";
import {
  type ContentCurrency,
  contentCurrencyFromCountry,
} from "./contentCurrency";

export { contentCurrencyFromCountry };

/**
 * One IP lookup per page load, shared by every hook that needs the visitor's
 * market. It is a network round trip whose answer cannot change mid-session,
 * and a product list renders one purchase card per row — without this, each row
 * would fetch its own copy.
 */
let geoLookup: Promise<GeoLocation> | null = null;

const lookupGeoOnce = (): Promise<GeoLocation> => {
  geoLookup ??= DateTimeMoment.getGeoLocation();
  return geoLookup;
};

/** Drops the shared lookup so each test starts from its own geo mock. */
export const resetGeoLookupForTests = (): void => {
  geoLookup = null;
};

export const isEcuadorCountry = (
  isoAlpha3?: string | null,
  name?: string | null
): boolean => {
  const iso = (isoAlpha3 ?? "").trim().toUpperCase();
  const country = (name ?? "").trim().toUpperCase();
  return iso === "ECU" || iso === "EC" || country === "ECUADOR";
};

type EcuadorMarketResolution = {
  isEcuador: boolean;
  /**
   * False only while an anonymous visitor's IP lookup is in flight. Callers that
   * would otherwise paint the US flag first and correct it a moment later can
   * withhold the flag until this turns true.
   */
  isResolved: boolean;
};

/**
 * Whether the visitor's market is Ecuador, which prices in USD like the United
 * States — so the currency alone cannot tell the two apart when naming a market
 * (a flag, say). The SSR seed only carries a currency, so USD still has to be
 * disambiguated here.
 *
 * An authenticated buyer's profile country wins and is never overridden; an
 * anonymous visitor falls back to the shared IP lookup, so an incognito window
 * in Ecuador is recognised too.
 */
export const useIsEcuadorMarket = (): EcuadorMarketResolution => {
  const { user } = useUser();
  const profileIsEcuador = isEcuadorCountry(
    user?.countryData?.isoAlpha3,
    user?.countryData?.name
  );
  const [anonymousIsEcuador, setAnonymousIsEcuador] = useState(false);
  const [isAnonymousGeoResolved, setIsAnonymousGeoResolved] = useState(false);

  useEffect(() => {
    if (user) return;

    let active = true;
    setIsAnonymousGeoResolved(false);

    void lookupGeoOnce()
      .then((geo) => {
        if (!active) return;
        setAnonymousIsEcuador(
          isEcuadorCountry(geo.country, geo.nameTranslated)
        );
      })
      .catch(() => {
        if (active) setAnonymousIsEcuador(false);
      })
      .finally(() => {
        if (active) setIsAnonymousGeoResolved(true);
      });

    return () => {
      active = false;
    };
  }, [user]);

  if (user) {
    return { isEcuador: profileIsEcuador, isResolved: true };
  }

  return {
    isEcuador: anonymousIsEcuador,
    isResolved: isAnonymousGeoResolved,
  };
};

export const useUserCurrency = (): ContentCurrency => {
  const { user } = useUser();
  return contentCurrencyFromCountry(
    user?.countryData?.isoAlpha3,
    user?.countryData?.name
  );
};

type UserCurrencyResolution = {
  currency: ContentCurrency;
  isResolved: boolean;
};

/**
 * Resolves the price currency without ever replacing an authenticated buyer's
 * profile country. Anonymous visitors use the same IPinfo-backed lookup as the
 * checkout; while it is pending, callers can withhold a USD fallback price and
 * avoid creating an explicit `?currency=USD` checkout link for a Colombian
 * visitor.
 */
export const useUserCurrencyResolution = (): UserCurrencyResolution => {
  const { user } = useUser();
  const profileCurrency = contentCurrencyFromCountry(
    user?.countryData?.isoAlpha3,
    user?.countryData?.name
  );
  // On server-rendered routes the visitor's currency is already resolved from
  // the request IP, so anonymous visitors skip the client geo round-trip that
  // would otherwise withhold the price until it lands.
  const { visitorCurrency } = useHomepageSsr();
  const [anonymousCurrency, setAnonymousCurrency] = useState<ContentCurrency>(
    visitorCurrency ?? "USD"
  );
  const [isAnonymousGeoResolved, setIsAnonymousGeoResolved] = useState(
    visitorCurrency !== undefined
  );

  useEffect(() => {
    if (user || visitorCurrency) return;

    let active = true;
    setIsAnonymousGeoResolved(false);

    void lookupGeoOnce()
      .then((geo) => {
        if (!active) return;
        setAnonymousCurrency(
          contentCurrencyFromCountry(geo.country, geo.nameTranslated)
        );
      })
      .catch(() => {
        if (active) setAnonymousCurrency("USD");
      })
      .finally(() => {
        if (active) setIsAnonymousGeoResolved(true);
      });

    return () => {
      active = false;
    };
  }, [user, visitorCurrency]);

  if (user) {
    return { currency: profileCurrency, isResolved: true };
  }

  return {
    currency: anonymousCurrency,
    isResolved: isAnonymousGeoResolved,
  };
};
